import { IComponentOptions, IPromise, IQService, IScope } from "angular";
import { StateService, Transition, TransitionService, UIRouterGlobals } from "@uirouter/angularjs";
import { take } from "rxjs";
import { GtmhubController } from "@gtmhub/core";
import { ApmSpanService } from "@gtmhub/core/tracing/apm-span.service";
import { IIndicator, UIErrorHandlingService } from "@gtmhub/error-handling";
import { FavoritesManager } from "@gtmhub/favorites/favorites.manager";
import { FavoritesManagerFactory } from "@gtmhub/favorites/favorites.manager.factory";
import {
  OKR_VIEWS_LIST_FILTERS_KEY,
  OKR_VIEWS_LIST_SCREEN_PATH,
  OKR_VIEWS_LIST_SETTING_KEY,
  OKR_VIEWS_LIST_SORTING_KEY,
  defaultGHGridColumns,
  generateDefaultFilters,
  generateDefaultSortingRules,
  okrViewsFilterDefinitions,
  okrViewsSortingDefinitions,
} from "@gtmhub/okrs/okr-views/utils/okr-views-all-utils";
import { BASE_OKR_VIEWS_STATE } from "@gtmhub/okrs/okrs.states";
import {
  GridFilterDefinitions,
  GridSortingDefinitions,
  IGridFiltersChange,
  IGridSortingRule,
  OrFiltersGroup,
  getPersistedScreenSettings,
  hasFiltersGroupsWithValues,
  setPersistedScreenSettings,
  toRestLayerRequest,
} from "@gtmhub/shared/components/grid-filtering-bar";
import { IGHGridColumn } from "@gtmhub/shared/components/grid/grid.component";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { RequestPaging } from "@webapp/core/abstracts/models/request.paging";
import { ICollection } from "@webapp/core/core.models";
import { ShareableFilter } from "@webapp/shared/unified-filters/models/shareable-filters.models";
import { ShareableFiltersFacade } from "@webapp/shared/unified-filters/services/shareable-filters/shareable-filters-facade.service";

const PAGING_SIZE = 20;
const FIRST_PAGE = 1;

export interface IOkrViewsListStateParams {
  page?: string;
}

export interface IOkrViewsAllScope extends IScope, IExtendedRootScopeService {
  itemsPerPage: number;
  filters: OrFiltersGroup[];
  filterDefinitions: GridFilterDefinitions;
  sortingRules: IGridSortingRule[];
  sortingDefinitions: GridSortingDefinitions;
  totalPages: number;
  currentPage: number;
  okrViews: ShareableFilter[];
  columns: IGHGridColumn[];
  showNoFilterResults: boolean;
  showEmptyState: boolean;
  indicators: {
    loading?: IIndicator;
    reloading?: boolean;
  };

  filtersChange(change: IGridFiltersChange): void;
  sortingChange(): void;
  onPageChange(page: number): void;
}

export class OkrViewsListCtrl extends GtmhubController {
  private okrViewsListTotalCount: number;
  private isFirstLoad = true;
  public favoritesManager: FavoritesManager;

  public static $inject = [
    "$q",
    "$scope",
    "$state",
    "$stateParams",
    "$transitions",
    "$uiRouterGlobals",
    "ShareableFiltersFacade",
    "ApmSpanService",
    "UIErrorHandlingService",
    "FavoritesManagerFactory",
  ];

  constructor(
    private $q: IQService,
    private $scope: IOkrViewsAllScope,
    private $state: StateService,
    private $stateParams: IOkrViewsListStateParams,
    private $transitions: TransitionService,
    private $uiRouterGlobals: UIRouterGlobals,
    private shareableFiltersFacade: ShareableFiltersFacade,
    private apmSpanService: ApmSpanService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private favoritesManagerFactory: FavoritesManagerFactory
  ) {
    super();
    this.favoritesManager = this.favoritesManagerFactory(["session", "filter"]);
  }

  public stateInit = (): void => {
    this.$scope.indicators = { loading: { progress: true } };

    const paramsPage = Number(this.$stateParams.page);
    this.$scope.currentPage = isNaN(paramsPage) ? FIRST_PAGE : paramsPage;
    this.$scope.itemsPerPage = PAGING_SIZE;

    this.$scope.sortingDefinitions = okrViewsSortingDefinitions();
    this.$scope.filterDefinitions = okrViewsFilterDefinitions();

    this.favoritesManager.initFavorites();

    this.initColumns();
    this.initFilters();
    this.initSorting();

    this.$scope.filtersChange = this.filtersChange;
    this.$scope.sortingChange = this.sortingChange;
    this.$scope.onPageChange = this.onPageChange;

    this.watchForPageChanges();

    this.onLoadOkrViewsList();
  };

  private initFilters = (): void => {
    const persistedFilters = this.getPersistedFilterSettings();

    if (persistedFilters.length) {
      this.$scope.filters = persistedFilters;
    } else {
      this.$scope.filters = generateDefaultFilters();
    }
  };

  private initSorting = () => {
    const persistedSortingRules = this.getPersistedSortingSettings();

    if (persistedSortingRules.length) {
      this.$scope.sortingRules = persistedSortingRules;
    } else {
      this.$scope.sortingRules = generateDefaultSortingRules();
    }
  };

  private setEmptyScreenAndNoFilterDisplay = (): void => {
    const anyFilterWithValuesInGroups = hasFiltersGroupsWithValues(this.$scope.filters);

    this.$scope.showNoFilterResults = this.noOkrViewsListFetched() && anyFilterWithValuesInGroups;
    this.$scope.showEmptyState = this.noOkrViewsListFetched() && !anyFilterWithValuesInGroups;
  };

  private noOkrViewsListFetched = (): boolean => !this.$scope.indicators.loading && !this.$scope.indicators.reloading && this.okrViewsListTotalCount === 0;

  private filtersChange = (change: IGridFiltersChange): void => {
    if (change.initial) return;

    setPersistedScreenSettings(OKR_VIEWS_LIST_SCREEN_PATH, OKR_VIEWS_LIST_FILTERS_KEY, this.$scope.filters);

    this.setCurrentPage(FIRST_PAGE);
  };

  private sortingChange = (): void => {
    setPersistedScreenSettings(OKR_VIEWS_LIST_SCREEN_PATH, OKR_VIEWS_LIST_SORTING_KEY, this.$scope.sortingRules);

    this.setCurrentPage(FIRST_PAGE);
  };

  private getPersistedFilterSettings = (): OrFiltersGroup[] => {
    return getPersistedScreenSettings(OKR_VIEWS_LIST_SCREEN_PATH, "okrViewsListFilters") || [];
  };

  private getPersistedSortingSettings = (): IGridSortingRule[] => {
    return getPersistedScreenSettings(OKR_VIEWS_LIST_SCREEN_PATH, "okrViewsListSortingRules") || [];
  };

  private setTotalPages = (totalCount: number) => {
    this.$scope.totalPages = Math.ceil(totalCount / PAGING_SIZE);

    if (this.$scope.totalPages && this.$scope.totalPages < this.$scope.currentPage) {
      this.setCurrentPage(this.$scope.totalPages);
    }
  };

  private setCurrentPage = (page?: number) => {
    const currentPage = page || FIRST_PAGE;
    this.changePageOrReloadOkrViews(currentPage);
  };

  private onPageChange = (page: number) => {
    this.changePageOrReloadOkrViews(page);
  };

  private watchForPageChanges = () => {
    const unsubscribePageChange = this.$transitions.onSuccess({ to: BASE_OKR_VIEWS_STATE }, (transition: Transition) => {
      const page = Number(transition.paramsChanged().page) || Number(transition.params().page) || FIRST_PAGE;
      if (page !== this.$scope.currentPage) {
        this.$scope.currentPage = page;
        this.onLoadOkrViewsList();
      }
    });
    this.$scope.$on("$destroy", unsubscribePageChange);
  };

  private changePageOrReloadOkrViews(page: number) {
    if (page !== this.$scope.currentPage || (this.$uiRouterGlobals.params.page && page !== Number(this.$uiRouterGlobals.params.page))) {
      this.$state.go(".", { page });
    } else {
      this.$scope.currentPage = page;

      this.onLoadOkrViewsList();
    }
  }

  private createQueryObject = (): RequestPaging => {
    const skip = (this.$scope.currentPage - 1) * PAGING_SIZE;
    const take = PAGING_SIZE;
    const filter = toRestLayerRequest({ filters: angular.copy(this.$scope.filters) }).filter;
    const sort = [toRestLayerRequest({ sorting: angular.copy(this.$scope.sortingRules) }).sort];
    const fields = ["id", "name", "ownerId", "access"];

    return { skip, take, fields, ...(filter && { filter }), ...(sort && { sort }) };
  };

  private onLoadOkrViewsList = (): IPromise<void> => {
    if (this.isFirstLoad) {
      return this.apmSpanService.dataLoadSpan("okrViewsList", (): IPromise<void> => {
        return this.loadOkrViewsList()
          .then(() => {
            this.isFirstLoad = false;
            delete this.$scope.indicators.loading;
            this.setEmptyScreenAndNoFilterDisplay();
          })
          .catch((err) => this.catchError(err));
      });
    }

    this.$scope.indicators.reloading = true;

    return this.loadOkrViewsList()
      .then(() => {
        this.$scope.indicators.reloading = false;
        this.setEmptyScreenAndNoFilterDisplay();
      })
      .catch((err) => this.catchError(err));
  };

  private loadOkrViewsList = (): IPromise<void> => {
    const queryObject = this.createQueryObject();

    return this.getOkrViewsList(queryObject).then((okrViews) => {
      this.$scope.okrViews = okrViews.items || [];
      this.okrViewsListTotalCount = okrViews.totalCount;
      this.setTotalPages(this.okrViewsListTotalCount);
    });
  };

  private getOkrViewsList = (okrViewsListFilter?: RequestPaging): IPromise<ICollection<ShareableFilter>> => {
    const okrViewsDefer = this.$q.defer<ICollection<ShareableFilter>>();

    this.shareableFiltersFacade
      .getShareableFilters$(okrViewsListFilter)
      .pipe(take(1))
      .subscribe({
        next: (okrViews) => {
          okrViewsDefer.resolve(okrViews);
        },
        error: (error) => {
          okrViewsDefer.reject(error);
        },
      });

    return okrViewsDefer.promise;
  };

  private initColumns = (): void => {
    this.$scope.columns = this.getOkrViewsGHGridColumns();
  };

  private getOkrViewsGHGridColumns = (): IGHGridColumn[] => {
    let columns: IGHGridColumn[];

    const persistedColumns = getPersistedScreenSettings(OKR_VIEWS_LIST_SCREEN_PATH, OKR_VIEWS_LIST_SETTING_KEY);

    if (!persistedColumns || persistedColumns.length === 0) {
      columns = defaultGHGridColumns();
      setPersistedScreenSettings(OKR_VIEWS_LIST_SCREEN_PATH, OKR_VIEWS_LIST_SETTING_KEY, columns);
    } else {
      columns = persistedColumns;
    }

    return columns;
  };

  private catchError = (error): void => {
    if (this.$scope.indicators.loading) {
      this.$scope.indicators.loading = { error: error.message };
    } else {
      this.$scope.indicators.reloading = false;
      this.uiErrorHandlingService.handleModal(error.message);
    }
  };
}

export const OkrViewsListComponent: IComponentOptions = {
  controller: OkrViewsListCtrl,
  template: require("./okr-views-list.component.html"),
  bindings: {},
};
